import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, FileDown, PlayCircle, Save } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table } from '@/components/ui/table'
import { FormField } from '@/components/FormField'
import { BankAccountPicker } from '@/components/BankAccountPicker'
import { Modal } from '@/components/Modal'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { formatDate } from '@/lib/locale'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import { useReportBrand, type ReportBrand } from '@/lib/pdf'
import { reportingApi } from '@/features/reporting/api'
import { usePostingStatus, useSaveSnapshot, useUnpostedPreview } from '@/features/reporting/queries'
import { usePostSelected } from '@/features/accruals/queries'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal } from '@/components/PostingSelectorModal'
import { formatCurrencyPrecise } from '@/lib/locale'
import {
  BalanceSheetReport,
  CashFlowReport,
  IncomeStatementReport,
  TrialBalanceReport,
} from '@/features/reporting/reports'
import { fmtMoney as fmt, formatLongDate } from '@/features/reporting/reports/format'
import type {
  BalanceSheetResponse,
  CashFlowResponse,
  DrillDownResponse,
  IncomeStatementResponse,
  TrialBalanceResponse,
} from '@/features/reporting/types'

type TabId = 'bs' | 'is' | 'cf' | 'tb'

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

async function maybeExportPdf(
  ref: HTMLTableElement | null,
  title: string,
  brand?: ReportBrand,
  subtitle?: string,
): Promise<boolean> {
  if (!ref) return false
  const mod = await import('@/features/reporting/export')
  return mod.downloadPdfFromTable(ref, title, brand, subtitle)
}
async function maybeExportCsv(
  ref: HTMLTableElement | null,
  filename: string,
  brand?: ReportBrand,
  title?: string,
  metaLines: string[] = [],
): Promise<boolean> {
  if (!ref) return false
  const mod = await import('@/features/reporting/export')
  return mod.downloadCsvFromTable(ref, filename, brand, title, metaLines)
}

const REPORT_TITLE: Record<TabId, string> = {
  bs: 'Balance Sheet',
  is: 'Income Statement',
  cf: 'Statement of Cash Flows',
  tb: 'Trial Balance',
}

// ─── Balance Sheet ───────────────────────────────────────────────────────

interface BSState {
  date1: string
  date2: string
  fiscalMonth: string
  bankAccountIds: number[]
  data: BalanceSheetResponse | null
  loading: boolean
}

function BalanceSheetTab({
  onShowDrillDown,
  onShowSnapshot,
}: {
  onShowDrillDown: (args: { mainCategory: string; subcategory: string; endDate: string; startDate?: string }) => void
  onShowSnapshot: (kind: TabId, periodStart: string | null, periodEnd: string, data: unknown) => void
}) {
  const today = todayIso()
  const regional = useRegionalDefaults()
  const brand = useReportBrand()
  const [s, setS] = useState<BSState>(() => ({
    date1: today,
    date2: '',
    fiscalMonth: String(regional.fiscalStartMonth),
    bankAccountIds: [],
    data: null,
    loading: false,
  }))
  // Once tenant settings load, swap if the user hasn't manually changed it.
  useEffect(() => {
    if (regional.isLoaded) {
      setS((x) => (x.fiscalMonth === '1' ? { ...x, fiscalMonth: String(regional.fiscalStartMonth) } : x))
    }
  }, [regional.isLoaded, regional.fiscalStartMonth])
  const tableRef = useRef<HTMLTableElement | null>(null)
  const { toast } = useToast()

  async function generate() {
    if (!s.date1) {
      toast({
        title: 'Date Required',
        description: 'Please select an "As of Date" for the Balance Sheet.',
        variant: 'destructive',
      })
      return
    }
    const dates = [s.date1]
    if (s.date2) dates.push(s.date2)
    setS((x) => ({ ...x, loading: true }))
    try {
      const data = await reportingApi.balanceSheet(dates, parseInt(s.fiscalMonth) || 1, s.bankAccountIds)
      setS((x) => ({ ...x, data, loading: false }))
    } catch (err) {
      setS((x) => ({ ...x, loading: false }))
      toast({
        title: 'Balance Sheet Error',
        description: err instanceof Error ? err.message : 'Failed to generate.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FormField
          id="bs-date1"
          label="As of Date"
          type="date"
          value={s.date1}
          onChange={(e) => setS({ ...s, date1: e.currentTarget.value })}
        />
        <FormField
          id="bs-date2"
          label="Compare (optional)"
          type="date"
          value={s.date2}
          onChange={(e) => setS({ ...s, date2: e.currentTarget.value })}
        />
        <FormField
          id="bs-fm"
          label="Fiscal Year Start"
          control={
            <Select value={s.fiscalMonth} onValueChange={(v) => setS({ ...s, fiscalMonth: v })}>
              <SelectTrigger id="bs-fm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((n, i) => (
                  <SelectItem key={n} value={String(i + 1)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <FormField
          label="Bank account"
          hint="Defaults to all accounts. Pick one or more to narrow."
          control={
            <BankAccountPicker
              mode="multi"
              value={s.bankAccountIds}
              onChange={(ids) => setS({ ...s, bankAccountIds: ids })}
              placeholder="All accounts"
            />
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" size="sm" onClick={generate} disabled={s.loading}>
          <PlayCircle /> Generate Balance Sheet
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={async () => {
            if (!s.data) return
            const mod = await import('@/features/reporting/export')
            mod.exportBalanceSheetPdf(s.data, brand, { fiscalStartMonth: parseInt(s.fiscalMonth) || 1 })
          }}
          disabled={!s.data}
        >
          <FileDown /> Download PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            void maybeExportCsv(tableRef.current, 'balance_sheet.csv', brand, REPORT_TITLE.bs, [
              `As of ${s.date1}${s.date2 ? ` vs ${s.date2}` : ''}`,
              `Fiscal year start: ${MONTH_NAMES[(parseInt(s.fiscalMonth) || 1) - 1]}`,
            ])
          }
          disabled={!s.data}
        >
          <Download /> Download CSV
        </Button>
      </div>

      {s.loading ? (
        <LoadingSpinner />
      ) : !s.data ? (
        <EmptyState title="Select parameters and click Generate" />
      ) : (
        <>
          <BalanceSheetReport data={s.data} tableRef={tableRef} onDrillDown={onShowDrillDown} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => onShowSnapshot('bs', null, s.date1, s.data)}
          >
            <Save /> Save as Snapshot
          </Button>
        </>
      )}
    </div>
  )
}


// ─── Income Statement ────────────────────────────────────────────────────

interface ISState {
  start: string
  end: string
  granularity: 'total' | 'monthly' | 'quarterly'
  fiscalMonth: string
  bankAccountIds: number[]
  data: IncomeStatementResponse | null
  loading: boolean
}

function IncomeStatementTab({
  onShowDrillDown,
  onShowSnapshot,
}: {
  onShowDrillDown: (args: { mainCategory: string; subcategory: string; endDate: string; startDate?: string }) => void
  onShowSnapshot: (kind: TabId, periodStart: string | null, periodEnd: string, data: unknown) => void
}) {
  const today = todayIso()
  const regional = useRegionalDefaults()
  const brand = useReportBrand()
  const [s, setS] = useState<ISState>(() => ({
    start: '',
    end: today,
    granularity: 'total',
    fiscalMonth: String(regional.fiscalStartMonth),
    bankAccountIds: [],
    data: null,
    loading: false,
  }))
  useEffect(() => {
    if (regional.isLoaded) {
      setS((x) => (x.fiscalMonth === '1' ? { ...x, fiscalMonth: String(regional.fiscalStartMonth) } : x))
    }
  }, [regional.isLoaded, regional.fiscalStartMonth])
  const tableRef = useRef<HTMLTableElement | null>(null)
  const { toast } = useToast()

  async function generate() {
    if (!s.start || !s.end) {
      toast({ title: 'Dates Required', description: 'Please select both Start and End dates.', variant: 'destructive' })
      return
    }
    if (new Date(s.start) > new Date(s.end)) {
      toast({ title: 'Invalid Date Range', description: 'Start Date must be before End Date.', variant: 'destructive' })
      return
    }
    setS((x) => ({ ...x, loading: true }))
    try {
      const data = await reportingApi.incomeStatement({
        startDate: s.start,
        endDate: s.end,
        granularity: s.granularity,
        fiscalStartMonth: parseInt(s.fiscalMonth) || 1,
        bankAccountIds: s.bankAccountIds.length > 0 ? s.bankAccountIds : undefined,
      })
      setS((x) => ({ ...x, data, loading: false }))
    } catch (err) {
      setS((x) => ({ ...x, loading: false }))
      toast({
        title: 'Income Statement Error',
        description: err instanceof Error ? err.message : 'Failed to generate.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <FormField
          id="is-start"
          label="Start Date"
          type="date"
          value={s.start}
          onChange={(e) => setS({ ...s, start: e.currentTarget.value })}
        />
        <FormField
          id="is-end"
          label="End Date"
          type="date"
          value={s.end}
          onChange={(e) => setS({ ...s, end: e.currentTarget.value })}
        />
        <FormField
          id="is-gran"
          label="Granularity"
          control={
            <Select value={s.granularity} onValueChange={(v) => setS({ ...s, granularity: v as ISState['granularity'] })}>
              <SelectTrigger id="is-gran">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total">Total</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          }
        />
        <FormField
          id="is-fm"
          label="Fiscal Year Start"
          control={
            <Select value={s.fiscalMonth} onValueChange={(v) => setS({ ...s, fiscalMonth: v })}>
              <SelectTrigger id="is-fm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((n, i) => (
                  <SelectItem key={n} value={String(i + 1)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <FormField
          label="Bank account"
          hint="Defaults to all accounts."
          control={
            <BankAccountPicker
              mode="multi"
              value={s.bankAccountIds}
              onChange={(ids) => setS({ ...s, bankAccountIds: ids })}
              placeholder="All accounts"
            />
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" size="sm" onClick={generate} disabled={s.loading}>
          <PlayCircle /> Generate Income Statement
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!s.data}
          onClick={async () => {
            if (!s.data) return
            const mod = await import('@/features/reporting/export')
            mod.exportIncomeStatementPdf(s.data, brand, { fiscalStartMonth: parseInt(s.fiscalMonth) || 1 })
          }}
        >
          <FileDown /> Download PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!s.data}
          onClick={() =>
            void maybeExportCsv(tableRef.current, 'income_statement.csv', brand, REPORT_TITLE.is, [
              `${s.start} – ${s.end}`,
              `Granularity: ${s.granularity}`,
              `Fiscal year start: ${MONTH_NAMES[(parseInt(s.fiscalMonth) || 1) - 1]}`,
            ])
          }
        >
          <Download /> Download CSV
        </Button>
      </div>

      {s.loading ? (
        <LoadingSpinner />
      ) : !s.data ? (
        <EmptyState title="Select parameters and click Generate" />
      ) : (
        <>
          <IncomeStatementReport
            data={s.data}
            tableRef={tableRef}
            onDrillDown={onShowDrillDown}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => onShowSnapshot('is', s.start, s.end, s.data)}
          >
            <Save /> Save as Snapshot
          </Button>
        </>
      )}
    </div>
  )
}


// ─── Cash Flow ───────────────────────────────────────────────────────────

interface CFState {
  start: string
  end: string
  fiscalMonth: string
  bankAccountIds: number[]
  data: CashFlowResponse | null
  loading: boolean
}

function CashFlowTab({
  onShowSnapshot,
}: {
  onShowSnapshot: (kind: TabId, periodStart: string | null, periodEnd: string, data: unknown) => void
}) {
  const today = todayIso()
  const regional = useRegionalDefaults()
  const brand = useReportBrand()
  const [s, setS] = useState<CFState>(() => ({
    start: '',
    end: today,
    fiscalMonth: String(regional.fiscalStartMonth),
    bankAccountIds: [],
    data: null,
    loading: false,
  }))
  useEffect(() => {
    if (regional.isLoaded) {
      setS((x) => (x.fiscalMonth === '1' ? { ...x, fiscalMonth: String(regional.fiscalStartMonth) } : x))
    }
  }, [regional.isLoaded, regional.fiscalStartMonth])
  const tableRef = useRef<HTMLTableElement | null>(null)
  const { toast } = useToast()

  async function generate() {
    if (!s.start || !s.end) {
      toast({ title: 'Dates Required', description: 'Please select both Start and End dates.', variant: 'destructive' })
      return
    }
    if (new Date(s.start) > new Date(s.end)) {
      toast({ title: 'Invalid Date Range', description: 'Start Date must be before End Date.', variant: 'destructive' })
      return
    }
    setS((x) => ({ ...x, loading: true }))
    try {
      const data = await reportingApi.cashFlow({
        startDate: s.start,
        endDate: s.end,
        fiscalStartMonth: parseInt(s.fiscalMonth) || 1,
        bankAccountIds: s.bankAccountIds.length > 0 ? s.bankAccountIds : undefined,
      })
      setS((x) => ({ ...x, data, loading: false }))
    } catch (err) {
      setS((x) => ({ ...x, loading: false }))
      toast({
        title: 'Cash Flow Error',
        description: err instanceof Error ? err.message : 'Failed to generate.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FormField
          id="cf-start"
          label="Start Date"
          type="date"
          value={s.start}
          onChange={(e) => setS({ ...s, start: e.currentTarget.value })}
        />
        <FormField
          id="cf-end"
          label="End Date"
          type="date"
          value={s.end}
          onChange={(e) => setS({ ...s, end: e.currentTarget.value })}
        />
        <FormField
          id="cf-fm"
          label="Fiscal Year Start"
          control={
            <Select value={s.fiscalMonth} onValueChange={(v) => setS({ ...s, fiscalMonth: v })}>
              <SelectTrigger id="cf-fm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((n, i) => (
                  <SelectItem key={n} value={String(i + 1)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <FormField
          label="Bank account"
          hint="Defaults to all accounts."
          control={
            <BankAccountPicker
              mode="multi"
              value={s.bankAccountIds}
              onChange={(ids) => setS({ ...s, bankAccountIds: ids })}
              placeholder="All accounts"
            />
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" size="sm" onClick={generate} disabled={s.loading}>
          <PlayCircle /> Generate Cash Flow
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!s.data}
          onClick={async () => {
            if (!s.data) return
            const mod = await import('@/features/reporting/export')
            mod.exportCashFlowPdf(s.data, brand, {
              startDate: s.start,
              endDate: s.end,
              fiscalStartMonth: parseInt(s.fiscalMonth) || 1,
            })
          }}
        >
          <FileDown /> Download PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!s.data}
          onClick={() =>
            void maybeExportCsv(tableRef.current, 'cash_flow.csv', brand, REPORT_TITLE.cf, [
              `${s.start} – ${s.end}`,
              `Fiscal year start: ${MONTH_NAMES[(parseInt(s.fiscalMonth) || 1) - 1]}`,
            ])
          }
        >
          <Download /> Download CSV
        </Button>
      </div>

      {s.loading ? (
        <LoadingSpinner />
      ) : !s.data ? (
        <EmptyState title="Select parameters and click Generate" />
      ) : (
        <>
          <CashFlowReport data={s.data} tableRef={tableRef} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => onShowSnapshot('cf', s.start, s.end, s.data)}
          >
            <Save /> Save as Snapshot
          </Button>
        </>
      )}
    </div>
  )
}


// ─── Trial Balance ───────────────────────────────────────────────────────

interface TBState {
  asOf: string
  bankAccountIds: number[]
  data: TrialBalanceResponse | null
  loading: boolean
}

function TrialBalanceTab({
  onShowSnapshot,
}: {
  onShowSnapshot: (kind: TabId, periodStart: string | null, periodEnd: string, data: unknown) => void
}) {
  const today = todayIso()
  const brand = useReportBrand()
  const [s, setS] = useState<TBState>({ asOf: today, bankAccountIds: [], data: null, loading: false })
  const tableRef = useRef<HTMLTableElement | null>(null)
  const { toast } = useToast()

  async function generate() {
    if (!s.asOf) {
      toast({ title: 'Date Required', description: 'Please select an "As of Date".', variant: 'destructive' })
      return
    }
    setS((x) => ({ ...x, loading: true }))
    try {
      const data = await reportingApi.trialBalance(s.asOf, s.bankAccountIds)
      setS((x) => ({ ...x, data, loading: false }))
    } catch (err) {
      setS((x) => ({ ...x, loading: false }))
      toast({
        title: 'Trial Balance Error',
        description: err instanceof Error ? err.message : 'Failed to generate.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FormField
          id="tb-date"
          label="As of Date"
          type="date"
          value={s.asOf}
          onChange={(e) => setS({ ...s, asOf: e.currentTarget.value })}
        />
        <FormField
          label="Bank account"
          hint="Defaults to all accounts."
          control={
            <BankAccountPicker
              mode="multi"
              value={s.bankAccountIds}
              onChange={(ids) => setS({ ...s, bankAccountIds: ids })}
              placeholder="All accounts"
            />
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="primary" size="sm" onClick={generate} disabled={s.loading}>
          <PlayCircle /> Generate Trial Balance
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!s.data}
          onClick={async () => {
            if (!s.data) return
            const mod = await import('@/features/reporting/export')
            mod.exportTrialBalancePdf(s.data, brand, { asOfDate: s.asOf })
          }}
        >
          <FileDown /> Download PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!s.data}
          onClick={() =>
            void maybeExportCsv(tableRef.current, 'trial_balance.csv', brand, REPORT_TITLE.tb, [
              `As of ${s.asOf}`,
            ])
          }
        >
          <Download /> Download CSV
        </Button>
      </div>

      {s.loading ? (
        <LoadingSpinner />
      ) : !s.data ? (
        <EmptyState title="Select an as-of date and click Generate" />
      ) : (
        <>
          <TrialBalanceReport data={s.data} tableRef={tableRef} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => onShowSnapshot('tb', null, s.asOf, s.data)}
          >
            <Save /> Save as Snapshot
          </Button>
        </>
      )}
    </div>
  )
}


// ─── Drill-down modal ──────────────────────────────────────────────────

interface DrillDownArgs {
  mainCategory: string
  subcategory: string
  endDate: string
  startDate?: string
}

function DrillDownModal({
  args,
  onClose,
}: {
  args: DrillDownArgs | null
  onClose: () => void
}) {
  const [data, setData] = useState<DrillDownResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const brand = useReportBrand()

  useEffect(() => {
    setData(null)
    setErr(null)
    if (!args) return
    let cancelled = false
    setLoading(true)
    reportingApi
      .drillDown({
        mainCategory: args.mainCategory,
        subcategory: args.subcategory,
        endDate: args.endDate,
        startDate: args.startDate,
      })
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [args])

  let running = (data?.openingBalance ?? 0) || 0

  // Filename + PDF-title base: "balance_sheet_cash_cash_as_of_2026_04_01".
  const exportBase = args
    ? `${args.mainCategory}_${args.subcategory}_as_of_${args.endDate}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    : 'account_details'
  // Why: split title vs subtitle so the right-aligned title slot in the
  // PDF header doesn't collide with the company-name block on the left
  // when the account name is long. The "as of" date is metadata, not
  // identity - it goes in the subtitle row.
  const pdfTitle = args ? `${args.mainCategory} – ${args.subcategory}` : 'Account Details'
  const pdfSubtitle = args ? `As of ${formatLongDate(args.endDate)}` : undefined

  const canExport = !!data && !loading

  return (
    <Modal
      open={args !== null}
      onOpenChange={(open) => !open && onClose()}
      title={args ? `${args.mainCategory} – ${args.subcategory}` : 'Details'}
      description={args ? `As of ${formatLongDate(args.endDate)}` : undefined}
      className="max-w-4xl"
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void maybeExportPdf(tableRef.current, pdfTitle, brand, pdfSubtitle)}
            disabled={!canExport}
          >
            <FileDown className="size-4" /> Download PDF
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void maybeExportCsv(tableRef.current, `${exportBase}.csv`, brand, pdfTitle)}
            disabled={!canExport}
          >
            <Download className="size-4" /> Download CSV
          </Button>
          <Button type="button" onClick={onClose}>Close</Button>
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner />
      ) : err ? (
        <p className="text-sm text-destructive">{err}</p>
      ) : !data ? null : (
        <div className="overflow-x-auto rounded-md border">
          <Table ref={tableRef} className="w-full min-w-[720px] text-sm">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-right">Debit</th>
                <th className="px-3 py-2 text-right">Credit</th>
                <th className="px-3 py-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t bg-muted/30 font-semibold">
                <td colSpan={3} className="px-3 py-1.5">
                  Opening Balance
                </td>
                <td />
                <td />
                <td className="px-3 py-1.5 text-right">{fmt(data.openingBalance)}</td>
              </tr>
              {(data.rows || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-3 text-center text-muted-foreground">
                    No entries found.
                  </td>
                </tr>
              ) : (
                data.rows.map((row, i) => {
                  const d = Number(row.debit) || 0
                  const c = Number(row.credit) || 0
                  running += d - c
                  return (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5">{formatDate(row.entry_date)}</td>
                      <td className="px-3 py-1.5">{row.description}</td>
                      <td className="px-3 py-1.5">{row.source_type}</td>
                      <td className="px-3 py-1.5 text-right">{d > 0 ? fmt(d) : ''}</td>
                      <td className="px-3 py-1.5 text-right">{c > 0 ? fmt(c) : ''}</td>
                      <td className="px-3 py-1.5 text-right">{fmt(running)}</td>
                    </tr>
                  )
                })
              )}
              <tr className="border-t-2 bg-muted/50 font-semibold">
                <td colSpan={3} className="px-3 py-1.5">
                  Closing Balance
                </td>
                <td />
                <td />
                <td className="px-3 py-1.5 text-right">{fmt(running)}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      )}
    </Modal>
  )
}

// ─── Page shell ────────────────────────────────────────────────────────

export function ReportingPage() {
  const [tab, setTab] = useState<TabId>('bs')
  const [drillArgs, setDrillArgs] = useState<DrillDownArgs | null>(null)
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const { data: postingStatus, refetch: refetchPosting } = usePostingStatus()
  const postSelectedMut = usePostSelected()
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)
  const saveSnapshot = useSaveSnapshot()
  const { toast } = useToast()

  // Why: the Reporting page is the only place that can post across
  // multiple sources in one go (a tabbed selector). Each source still
  // goes through its own /post-selected request - no global "post all"
  // shortcut on the server - so the user remains in control of what
  // hits the GL. Posting one source NEVER affects another.
  const sourceTabs = useMemo(() => {
    const data = unpostedPreview.data
    if (!data) return []
    const bsRows = data.bankStatements.map((r) => ({
      id: r.id,
      cells: [
        formatLongDate(r.transactionDate),
        <span className="block max-w-[420px] truncate" title={r.description ?? undefined}>
          {r.description || '(no description)'}
        </span>,
        r.classifiedSubcategory || r.classifiedMainCategory || '-',
        formatCurrencyPrecise(r.amount),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Closed period or pre-TB-cutoff' : undefined,
    }))
    const accRows = data.accruals.map((r) => ({
      id: r.id,
      cells: [
        r.date ? formatLongDate(r.date) : '-',
        r.supplier || '-',
        r.accrualType || '-',
        <span className="block max-w-[280px] truncate" title={r.description ?? undefined}>
          {r.description || '-'}
        </span>,
        formatCurrencyPrecise(r.amount),
      ],
      blocked: false,
    }))
    const jeRows = data.journalEntries.map((r) => ({
      id: r.id,
      cells: [
        r.entryDate ? formatLongDate(r.entryDate) : '-',
        <span className="font-mono text-xs">{r.entryNumber || `JE #${r.id}`}</span>,
        <span className="block max-w-[420px] truncate" title={r.description ?? undefined}>
          {r.description || '-'}
        </span>,
        formatCurrencyPrecise(r.totalAmount),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
    }))
    const schedRowsFor = (kind: 'depreciation' | 'prepaid' | 'loan') =>
      data.schedules
        .filter((r) => r.scheduleType === kind)
        .map((r) => ({
          id: r.id,
          cells: [
            formatLongDate(r.entryDate),
            <span className="block max-w-[320px] truncate" title={r.scheduleDescription ?? undefined}>
              {r.scheduleDescription || r.category || kind}
            </span>,
            `Period ${r.periodNumber}`,
            <span className="text-xs text-muted-foreground">
              Dr {r.debitAccount || '-'} / Cr {r.creditAccount || '-'}
            </span>,
            formatCurrencyPrecise(r.amount),
          ],
          blocked: r.blocked,
          blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
        }))
    const invoiceRows = (data.invoices ?? []).map((r) => ({
      id: r.id,
      cells: [
        formatLongDate(r.issueDate),
        <span className="font-mono text-xs">{r.invoiceNumber || `INV #${r.id}`}</span>,
        r.customerName || '-',
        r.status ? <Badge variant="secondary" className="capitalize">{String(r.status).replace(/_/g, ' ')}</Badge> : '-',
        formatCurrencyPrecise(r.total),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
    }))
    const billRows = (data.bills ?? []).map((r) => ({
      id: r.id,
      cells: [
        formatLongDate(r.billDate),
        <span className="font-mono text-xs">{r.billNumber || `BILL #${r.id}`}</span>,
        r.vendorName || '-',
        r.status ? <Badge variant="secondary" className="capitalize">{String(r.status).replace(/_/g, ' ')}</Badge> : '-',
        formatCurrencyPrecise(r.total),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
    }))
    return [
      {
        key: 'bank', label: 'Bank statements', items: bsRows,
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Description' },
          { header: 'Category' },
          { header: 'Amount', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'accruals', label: 'Accruals', items: accRows,
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Supplier' },
          { header: 'Type' },
          { header: 'Description' },
          { header: 'Amount', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'journal', label: 'Journal entries', items: jeRows,
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Entry #', width: 'w-32' },
          { header: 'Description' },
          { header: 'Total', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'depreciation', label: 'Depreciation', items: schedRowsFor('depreciation'),
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Description' },
          { header: 'Period', width: 'w-24' },
          { header: 'Accounts' },
          { header: 'Amount', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'prepaid', label: 'Prepaid', items: schedRowsFor('prepaid'),
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Description' },
          { header: 'Period', width: 'w-24' },
          { header: 'Accounts' },
          { header: 'Amount', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'loan', label: 'Loan', items: schedRowsFor('loan'),
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Description' },
          { header: 'Period', width: 'w-24' },
          { header: 'Accounts' },
          { header: 'Amount', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'invoices', label: 'Invoices', items: invoiceRows,
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Invoice #', width: 'w-32' },
          { header: 'Customer' },
          { header: 'Status', width: 'w-32' },
          { header: 'Total', align: 'right' as const, width: 'w-28' },
        ],
      },
      {
        key: 'bills', label: 'Bills', items: billRows,
        columns: [
          { header: 'Date', width: 'w-32' },
          { header: 'Bill #', width: 'w-32' },
          { header: 'Vendor' },
          { header: 'Status', width: 'w-32' },
          { header: 'Total', align: 'right' as const, width: 'w-28' },
        ],
      },
    ]
  }, [unpostedPreview.data])

  async function handlePostSelected(selection: Record<string, number[]>) {
    const sources = Object.entries(selection).filter(([, ids]) => ids.length > 0)
    if (sources.length === 0) return
    let totalPosted = 0
    let totalSkipped = 0
    let totalPendingApproval = 0
    const errors: string[] = []
    // Why: one request per source so a failure in one (e.g. bank statements
    // hitting a closed period) doesn't abort the others. The user sees a
    // partial-success toast.
    for (const [src, ids] of sources) {
      try {
        const r = await postSelectedMut.mutateAsync({ source: src as never, ids })
        totalPosted += r.posted ?? 0
        totalSkipped += r.skipped ?? 0
        totalPendingApproval += r.pendingApproval ?? 0
      } catch (err) {
        errors.push(`${src}: ${err instanceof Error ? err.message : 'failed'}`)
      }
    }
    // Refetch the reporting banner count AND wait a tick so the
    // mutation's invalidation propagates to the unposted-preview cache
    // before we close the modal - otherwise re-opening immediately
    // shows the stale (pre-post) row list.
    await refetchPosting()
    if (totalPosted > 0) {
      const bits: string[] = []
      if (totalSkipped > 0) bits.push(`${totalSkipped} skipped`)
      if (totalPendingApproval > 0) bits.push(`${totalPendingApproval} routed to approval`)
      const suffix = bits.length ? ` · ${bits.join(' · ')}.` : '.'
      toast({
        title: 'Transactions Posted',
        description: `Posted ${totalPosted} item${totalPosted === 1 ? '' : 's'} to the GL${suffix}`,
      })
    } else if (totalPendingApproval > 0) {
      toast({
        title: 'Routed to approval',
        description: `${totalPendingApproval} item${totalPendingApproval === 1 ? '' : 's'} need approval before posting. Track them in the Approvals inbox.`,
      })
    }
    if (errors.length > 0) {
      toast({
        title: 'Some sources failed to post',
        description: errors.join(' · '),
        variant: 'destructive',
      })
    } else if (totalPosted === 0 && totalPendingApproval === 0) {
      toast({
        title: 'Nothing posted',
        description: 'The selected entries are blocked by closed periods or were already posted.',
      })
    }
    setPostSelectorOpen(false)
  }

  async function handleSaveSnapshot(kind: TabId, periodStart: string | null, periodEnd: string, data: unknown) {
    try {
      await saveSnapshot.mutateAsync({
        reportType: REPORT_TITLE[kind],
        periodStart,
        periodEnd,
        fiscalYear: new Date(periodEnd).getFullYear(),
        reportData: data,
      })
      toast({
        title: 'Snapshot saved',
        description: 'View it in Reporting Config → Report Snapshots.',
      })
    } catch (err) {
      toast({
        title: 'Snapshot failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const unposted = postingStatus?.unposted ?? 0
  const blocked = postingStatus?.blocked ?? 0

  return (
    <Page
      title="Financial Reports"
      description="Generate Balance Sheet, Income Statement, Cash Flow, and Trial Balance."
      width="wide"
    >
      <PostingBanner
        count={unposted}
        label="transactions"
        singularLabel="transaction"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />
      {unposted === 0 && blocked > 0 ? (
        <Alert>
          <AlertDescription>
            <strong>{blocked}</strong> transactions can&rsquo;t be posted because their reporting
            period is closed or they fall before your TB-import cutoff. Reopen the period or adjust
            the cutoff to include them.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="p-4 sm:p-5">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="bs">Balance Sheet</TabsTrigger>
            <TabsTrigger value="is">Income Statement</TabsTrigger>
            <TabsTrigger value="cf">Cash Flow</TabsTrigger>
            <TabsTrigger value="tb">Trial Balance</TabsTrigger>
          </TabsList>
          <TabsContent value="bs" className="mt-4">
            <BalanceSheetTab onShowDrillDown={setDrillArgs} onShowSnapshot={handleSaveSnapshot} />
          </TabsContent>
          <TabsContent value="is" className="mt-4">
            <IncomeStatementTab onShowDrillDown={setDrillArgs} onShowSnapshot={handleSaveSnapshot} />
          </TabsContent>
          <TabsContent value="cf" className="mt-4">
            <CashFlowTab onShowSnapshot={handleSaveSnapshot} />
          </TabsContent>
          <TabsContent value="tb" className="mt-4">
            <TrialBalanceTab onShowSnapshot={handleSaveSnapshot} />
          </TabsContent>
        </Tabs>
      </Card>

      <DrillDownModal args={drillArgs} onClose={() => setDrillArgs(null)} />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending transactions to post' : 'Post pending transactions'}
        description={selectorReadOnly
          ? 'These rows are queued to post across every source. Click Post Now to choose which to post.'
          : 'Each tab is one source. Tick the rows you want to post. Posting one source never affects another - pick exactly what should land on the GL right now.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={sourceTabs}
        onPost={handlePostSelected}
      />
    </Page>
  )
}
